import { useMutation } from '@apollo/client';
import type { ApolloCache } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { cloneDeep } from 'lodash';

import type { TrackingData, TrackingProps } from 'js/lib/retracked';
import User from 'js/lib/user';

import type { CoursePlanDialogType } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/context/CoursePlanningDialogContext';
import type {
  UseCurriculum_LearnerProgressFragment as LearnerProgress,
  UseCurriculum_PersonalizedSegmentCourseFragment as PersonalizedSegmentCourse,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import type {
  DegreePlan_PlannedSegmentCoursesFragment as PlannedSegmentCourse,
  DegreePlan_TermPlanFragment as TermPlan,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/learnerPlanFragment';
import type {
  RemoveTermMutation as RemoveTermMutationData,
  RemoveTermMutationVariables as RemoveTermVariables,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/removePlannedTerm';
import type {
  UpdateTermMutation as UpdateTermMutationData,
  UpdateTermMutationVariables as UpdateTermVariables,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/updatePlannedTermMutation';
import type {
  UseCurriculum_DegreeCurriculumFragment as DegreeCurriculumFragment,
  UseCurriculum_PbaCurriculumFragment as PbaCurriculumFragment,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import type { UseDegreePlanQuery as DegreePlanData } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useDegreePlan';
import RemoveTermMutation from 'bundles/degree-home/components/v3-dpm/lib/__queries__/removePlannedTerm.graphql';
import UpdateTermMutation from 'bundles/degree-home/components/v3-dpm/lib/__queries__/updatePlannedTermMutation.graphql';
import UseCurriculumQuery from 'bundles/degree-home/components/v3-dpm/lib/__queries__/useCurriculum.graphql';
import { isDegreeCurriculum } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';

export const EMPTY_TERM_PLAN = {
  __typename: 'LearnerPlan_TermPlan' as const,
  estimatedTermName: '',
  linkedTerm: null,
  plannedSegmentCourses: [],
  autoCorrected: false,
  estimatedTermId: '',
  exceedsTimeAllowed: false,
  skipped: false,
};

export function getUpdatedPlannedSegmentCoursesIds(
  existingTerm: TermPlan | undefined,
  currentCourseId: string
): string[] {
  let updatedPlannedSegmentCoursesIds: string[] = [];

  if (existingTerm?.plannedSegmentCourses) {
    updatedPlannedSegmentCoursesIds = existingTerm.plannedSegmentCourses
      .map((course) => course.personalizedSegmentCourse?.segmentCourse?.id)
      .filter((id): id is string => id !== undefined);

    if (!updatedPlannedSegmentCoursesIds.includes(currentCourseId)) {
      updatedPlannedSegmentCoursesIds.push(currentCourseId);
    }
  } else {
    updatedPlannedSegmentCoursesIds = [currentCourseId];
  }

  return updatedPlannedSegmentCoursesIds;
}

export const createPlannedSegmentCourse = (currentCourse: PersonalizedSegmentCourse): PlannedSegmentCourse => {
  const personalizedSegmentCourse: PersonalizedSegmentCourse = cloneDeep(currentCourse);
  personalizedSegmentCourse.learnerProgress = { ...personalizedSegmentCourse.learnerProgress, state: 'PLANNED' };

  return {
    __typename: 'LearnerPlan_PlannedSegmentCourse' as const,
    availabilityUnconfirmed: false,
    needsReplan: false,
    personalizedSegmentCourse: { ...personalizedSegmentCourse },
  };
};
export const getSelectedTermDetails = (
  learnerPlan: TermPlan[],
  selectedTermId: string,
  currentCourse: PersonalizedSegmentCourse
) => {
  const selectedTermInLearnerPlanIndex = learnerPlan.findIndex((t) => t.estimatedTermId === selectedTermId);
  const isSelectedTermInPlan = selectedTermInLearnerPlanIndex === -1;

  const selectedTermDetails = (currentCourse.segmentCourse.termAvailabilityPaginated?.elements || []).find(
    (currentTerm) => currentTerm.estimatedTermId === selectedTermId
  );

  return { selectedTermInLearnerPlanIndex, isSelectedTermInPlan, selectedTermDetails };
};

export const removeCourseFromTerm = (
  learnerPlan: TermPlan[],
  selectedTermId: string,
  currentCourse: PersonalizedSegmentCourse
): TermPlan[] | null => {
  const updatedPlan = cloneDeep(learnerPlan);

  const { selectedTermInLearnerPlanIndex } = getSelectedTermDetails(learnerPlan, selectedTermId, currentCourse);

  const plannedSegmentCourses = updatedPlan[selectedTermInLearnerPlanIndex].plannedSegmentCourses;
  if (selectedTermInLearnerPlanIndex === -1) return null;

  updatedPlan[selectedTermInLearnerPlanIndex].plannedSegmentCourses = (plannedSegmentCourses || []).filter(
    (course: PlannedSegmentCourse) =>
      //  using course code here in order to delete all instances of a segment course PBA and Degree https://coursera.atlassian.net/browse/CPM-2305
      course.personalizedSegmentCourse?.segmentCourse?.course.courseCode !==
      currentCourse.segmentCourse?.course.courseCode
  );

  // if the course was the last planned course in the term, also delete the corresponding term
  if (!updatedPlan[selectedTermInLearnerPlanIndex].plannedSegmentCourses?.length) {
    updatedPlan.splice(selectedTermInLearnerPlanIndex, 1);
  }

  return updatedPlan;
};

export const addCourseToTerm = (
  learnerPlan: TermPlan[],
  selectedTermId: string,
  currentCourse: PersonalizedSegmentCourse
): TermPlan[] => {
  const updatedPlan = cloneDeep(learnerPlan);
  const plannedSegmentCourse = createPlannedSegmentCourse(currentCourse);

  const { selectedTermInLearnerPlanIndex, isSelectedTermInPlan, selectedTermDetails } = getSelectedTermDetails(
    learnerPlan,
    selectedTermId,
    currentCourse
  );

  // if term does not exist on learner plan add new term to the plan
  if (isSelectedTermInPlan) {
    updatedPlan.push({
      ...EMPTY_TERM_PLAN,
      estimatedTermId: selectedTermId,
      estimatedTermName: selectedTermDetails?.estimatedTermName || '',
      linkedTerm: selectedTermDetails?.linkedTerm || null,
      plannedSegmentCourses: [plannedSegmentCourse],
    });
  } else {
    const plannedSegmentCourses = updatedPlan[selectedTermInLearnerPlanIndex].plannedSegmentCourses ?? [];

    plannedSegmentCourses.push(plannedSegmentCourse);
  }

  return updatedPlan;
};

export const moveCourseToTerm = (
  learnerPlan: TermPlan[],
  destinationId: string,
  sourceId: string,
  currentCourse: PersonalizedSegmentCourse
): TermPlan[] | null => {
  const planWithCourseRemoved = removeCourseFromTerm(learnerPlan, sourceId, currentCourse);

  if (!planWithCourseRemoved) return null;

  const updatedPlan = addCourseToTerm(planWithCourseRemoved, destinationId, currentCourse);

  return updatedPlan;
};

export const updateCurriculumInCache = (
  cache: ApolloCache<unknown>,
  segmentCourseIdsToUpdate: Set<string>,
  newProgressStateToUpdate: LearnerProgress['state'] | null,
  degreeId: string
) => {
  const userId = `${User.get().id}`;
  const existingCurriculum = cache.readQuery({
    query: UseCurriculumQuery,
    variables: {
      degreeId,
      userId,
    },
  });
  if (existingCurriculum) {
    const newCurriculum = JSON.parse(JSON.stringify(existingCurriculum));

    if (
      segmentCourseIdsToUpdate.size > 0 &&
      !!newProgressStateToUpdate &&
      newCurriculum?.Degree?.queryById?.curriculum?.curriculums
    ) {
      newCurriculum.Degree.queryById.curriculum.curriculums.forEach(
        (curriculum: DegreeCurriculumFragment | PbaCurriculumFragment) => {
          curriculum.categories.forEach((category) => {
            category.segments.forEach((segment) => {
              segment.courses.forEach((course) => {
                if (segmentCourseIdsToUpdate.has(course.segmentCourse.id)) {
                  const updatedCourse = { ...course };
                  updatedCourse.learnerProgress.state = newProgressStateToUpdate;
                }
              });
            });
          });
        }
      );

      cache.writeQuery({
        query: UseCurriculumQuery,
        variables: {
          degreeId,
          userId,
        },
        data: newCurriculum,
      });
    }
  }
};

export function useUpdateTermMutation() {
  const [updateTermMutation, { loading: updateMutationLoading }] = useMutation<
    UpdateTermMutationData,
    UpdateTermVariables
  >(UpdateTermMutation, {
    context: { clientName: 'gatewayGql' },
  });
  const [removeTermMutation, { loading: removeMutationLoading }] = useMutation<
    RemoveTermMutationData,
    RemoveTermVariables
  >(RemoveTermMutation, {
    context: { clientName: 'gatewayGql' },
  });
  const removeTerm = ({
    currentCourse,
    degreeId,
    selectedTermId,
    version,
    refetchCurriculum,
    refetchDegreePlan,
    openCourseDialog,
    dialogType,
    track,
    trackingData,
    optimisticResponse,
  }: {
    currentCourse: PersonalizedSegmentCourse;
    degreeId: string;
    selectedTermId: string;
    version: number;
    refetchCurriculum: () => void;
    refetchDegreePlan: () => void;
    openCourseDialog: (course: PersonalizedSegmentCourse | null, dialogType: CoursePlanDialogType) => void;
    dialogType: CoursePlanDialogType;
    track?: (p: TrackingProps) => void;
    trackingData: TrackingData | undefined;
    optimisticResponse: TermPlan[] | null;
  }) => {
    removeTermMutation({
      variables: {
        input: {
          degreeId,
          estimatedTermId: selectedTermId,
          version,
        },
      },
      optimisticResponse: optimisticResponse
        ? {
            /* eslint-disable camelcase */
            LearnerPlan_RemoveTerm: {
              __typename: 'LearnerPlan_LearnerPlanResult',
              learnerPlan: optimisticResponse,
              version,
            },
          }
        : {},
      update: (cache) => {
        updateCurriculumInCache(
          cache,
          currentCourse ? new Set([currentCourse.segmentCourse.id]) : new Set(),
          'NOT_STARTED',
          degreeId
        );
      },
    })
      .then((result) => {
        const hasGqlError = result?.data?.LearnerPlan_RemoveTerm?.__typename !== 'LearnerPlan_LearnerPlanResult';
        if (!result || hasGqlError || result?.errors) {
          openCourseDialog(null, 'error');
        } else {
          if (dialogType === 'remove' && track && trackingData) {
            track({
              action: 'click',
              trackingName: 'remove_course',
              trackingData,
            });
          }

          refetchCurriculum();
          refetchDegreePlan();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        openCourseDialog(null, 'error');
      });
  };

  const updateTerm = ({
    degreePlanData,
    degreeId,
    selectedTermId,
    updatedPlannedSegmentCoursesIds,
    version,
    currentCourse,
    refetchCurriculum,
    refetchDegreePlan,
    openCourseDialog,
    dialogType,
    track,
    trackingData,
  }: {
    degreePlanData: DegreePlanData;
    degreeId: string;
    selectedTermId: string;
    updatedPlannedSegmentCoursesIds: string[];
    version: number;
    currentCourse: PersonalizedSegmentCourse;
    refetchCurriculum: () => void;
    refetchDegreePlan: () => void;
    openCourseDialog: (course: PersonalizedSegmentCourse | null, dialogType: CoursePlanDialogType) => void;
    dialogType: CoursePlanDialogType;
    track?: (p: TrackingProps) => void;
    trackingData?: TrackingData | undefined;
  }) => {
    let newProgressStateToUpdate: LearnerProgress['state'] | null = null;
    const learnerPlan = degreePlanData.LearnerPlan?.queryPlan?.learnerPlan || [];
    let optimisticResponse = null;

    if (dialogType === 'add') {
      newProgressStateToUpdate = 'PLANNED';
      optimisticResponse = addCourseToTerm(learnerPlan, selectedTermId, currentCourse);
    } else if (dialogType === 'remove') {
      newProgressStateToUpdate = 'NOT_STARTED';
      optimisticResponse = removeCourseFromTerm(learnerPlan, selectedTermId, currentCourse);
    }
    /* if the course to be removed is the last planned course in the plan (i.e. after the removal,
       there are no planned courses), execute the mutation to remove the term plan directly */
    if (updatedPlannedSegmentCoursesIds.length === 0 && dialogType === 'remove') {
      return removeTerm({
        degreeId,
        selectedTermId,
        version,
        refetchCurriculum,
        currentCourse,
        refetchDegreePlan,
        openCourseDialog,
        dialogType,
        track,
        trackingData,
        optimisticResponse,
      });
    }

    updateTermMutation({
      variables: {
        input: {
          degreeId,
          estimatedTermId: selectedTermId,
          skipped: false, // TODO: handle skipped terms
          plannedSegmentCourses: updatedPlannedSegmentCoursesIds,
          version,
        },
      },
      optimisticResponse: optimisticResponse
        ? {
            /* eslint-disable camelcase */
            LearnerPlan_UpdateTerm: {
              __typename: 'LearnerPlan_LearnerPlanResult',
              learnerPlan: optimisticResponse,
              version,
            },
          }
        : {},
      update: (cache) => {
        updateCurriculumInCache(
          cache,
          currentCourse ? new Set([currentCourse.segmentCourse.id]) : new Set(),
          newProgressStateToUpdate,
          degreeId
        );
      },
    })
      .then((result) => {
        const hasGqlError = result?.data?.LearnerPlan_UpdateTerm?.__typename !== 'LearnerPlan_LearnerPlanResult';
        if (!result || hasGqlError || result?.errors) {
          openCourseDialog(null, 'error');
        } else {
          refetchCurriculum();
          refetchDegreePlan();
        }
        return result;
      })
      .catch((error) => {
        Sentry.captureException(error);
        openCourseDialog(null, 'error');
        return error;
      });
    return null;
  };

  return { updateTerm, updateMutationLoading, removeMutationLoading };
}

export const isCoursePlannable = (segmentCourse: PersonalizedSegmentCourse): boolean =>
  segmentCourse.learnerProgress.state === 'NOT_STARTED';

/**
 * Given a list of curriculums, extract the list of ids of the plannable segment courses (from the degree curriculum)
 * @param curriculums the list of curriculums
 * @returns the list of the ids of the plannable segment courses
 */
export const getPlannableCourseIdsFromDegreeCurriculum = (
  curriculums: (DegreeCurriculumFragment | PbaCurriculumFragment)[]
): string[] => {
  const degreeCurriculum = curriculums.find((curriculum) => isDegreeCurriculum(curriculum));
  const plannableCourseIds = (degreeCurriculum?.categories ?? [])
    .map((category) => category.segments)
    .flat()
    .map((segment) => segment.courses)
    .flat()
    .filter((course) => isCoursePlannable(course))
    .map((course) => course.segmentCourse.id);
  return plannableCourseIds;
};
